import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { Admin, AdminLoginRequest, AdminAuthResponse } from '../models/admin.interface';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentAdminSubject = new BehaviorSubject<Admin | null>(null);
  public currentAdmin$ = this.currentAdminSubject.asObservable();

  private readonly TOKEN_KEY = 'admin_token';
  private readonly ADMIN_KEY = 'admin_user';
  private readonly API_URL = `${environment.apiUrl}/admin/auth`;

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    // Initialize admin from localStorage on service creation
    this.initializeAdmin();
  }

  private initializeAdmin(): void {
    const storedAdmin = localStorage.getItem(this.ADMIN_KEY);
    const token = localStorage.getItem(this.TOKEN_KEY);
    
    if (storedAdmin && token) {
      try {
        const admin = JSON.parse(storedAdmin);
        this.currentAdminSubject.next(admin);
        
        // Don't automatically verify token on startup - let the auth interceptor handle token validation
        // If token is invalid, subsequent API calls will fail and trigger logout
        console.log('Admin session restored from localStorage:', admin.email);
      } catch (error) {
        console.error('Error parsing stored admin data:', error);
        this.clearLocalStorage();
      }
    }
  }

  /**
   * Login admin with credentials
   */
  login(credentials: AdminLoginRequest): Observable<boolean> {
    return this.http.post<any>(`${this.API_URL}/login`, credentials)
      .pipe(
        map((response: any) => {
          if (response && response.success && response.data && response.data.token) {
            localStorage.setItem(this.TOKEN_KEY, response.data.token);
            localStorage.setItem(this.ADMIN_KEY, JSON.stringify(response.data.admin));
            this.currentAdminSubject.next(response.data.admin);
            return true;
          }
          return false;
        }),
        catchError((error) => {
          console.error('Login error:', error);
          return of(false);
        })
      );
  }

  /**
   * Logout admin
   */
  logout(silent: boolean = false): Observable<boolean> {
    const token = this.getToken();
    const admin = this.getCurrentAdmin();
    
    if (!silent && admin) {
      console.log(`Logging out admin: ${admin.email}`);
    }
    
    // Always clear local storage first
    this.clearLocalStorage();
    
    if (token && !silent) {
      // Attempt to call logout endpoint (but don't wait for it)
      this.http.post(`${this.API_URL}/logout`, {}).subscribe({
        next: (response: any) => {
          console.log('Server logout successful:', response.message || 'Logged out');
        },
        error: (error) => {
          console.warn('Server logout failed (but local logout completed):', error.message);
        }
      });
    }
    
    // Navigate to login page
    this.router.navigate(['/auth/login'], { 
      queryParams: { message: silent ? null : 'logged_out' }
    });
    
    return of(true);
  }

  /**
   * Logout with confirmation dialog
   */
  logoutWithConfirmation(): Observable<boolean> {
    return new Observable(observer => {
      const confirmed = window.confirm('Are you sure you want to logout?');
      if (confirmed) {
        this.logout().subscribe({
          next: (success) => {
            observer.next(success);
            observer.complete();
          },
          error: (error) => {
            observer.error(error);
          }
        });
      } else {
        observer.next(false);
        observer.complete();
      }
    });
  }

  /**
   * Force logout (for security scenarios)
   */
  forceLogout(reason: string = 'Security logout'): void {
    console.warn('Force logout triggered:', reason);
    this.clearLocalStorage();
    this.router.navigate(['/auth/login'], { 
      queryParams: { 
        message: 'session_expired',
        reason: reason
      }
    });
  }

  /**
   * Get current admin profile
   */
  getProfile(): Observable<{ success: boolean; admin: Admin }> {
    return this.http.get<{ success: boolean; admin: Admin }>(`${this.API_URL}/me`)
      .pipe(
        tap(response => {
          if (response.success) {
            localStorage.setItem(this.ADMIN_KEY, JSON.stringify(response.admin));
            this.currentAdminSubject.next(response.admin);
          }
        })
      );
  }

  /**
   * Verify token validity
   */
  verifyToken(): Observable<boolean> {
    return this.http.get<{ valid: boolean }>(`${this.API_URL}/verify`)
      .pipe(
        map(response => response.valid),
        catchError(() => of(false))
      );
  }

  /**
   * Refresh token
   */
  refreshToken(): Observable<{ success: boolean; token?: string }> {
    return this.http.post<{ success: boolean; token?: string }>(`${this.API_URL}/refresh`, {})
      .pipe(
        tap(response => {
          if (response.success && response.token) {
            localStorage.setItem(this.TOKEN_KEY, response.token);
          }
        })
      );
  }

  /**
   * Change password
   */
  changePassword(passwordData: { currentPassword: string; newPassword: string; confirmPassword: string }): Observable<{ success: boolean; message: string }> {
    return this.http.put<{ success: boolean; message: string }>(`${this.API_URL}/change-password`, passwordData);
  }

  /**
   * Update profile
   */
  updateProfile(profileData: { fullName?: string; profilePicture?: string }): Observable<{ success: boolean; admin: Admin; message: string }> {
    return this.http.put<{ success: boolean; admin: Admin; message: string }>(`${this.API_URL}/profile`, profileData)
      .pipe(
        tap(response => {
          if (response.success) {
            localStorage.setItem(this.ADMIN_KEY, JSON.stringify(response.admin));
            this.currentAdminSubject.next(response.admin);
          }
        })
      );
  }

  // ================== UTILITY METHODS ==================

  /**
   * Get stored token
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Check if admin is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    const admin = this.currentAdminSubject.value;
    return !!(token && admin);
  }

  /**
   * Get current admin
   */
  getCurrentAdmin(): Admin | null {
    return this.currentAdminSubject.value;
  }

  /**
   * Check if current admin has specific role
   */
  hasRole(role: string | string[]): boolean {
    const admin = this.getCurrentAdmin();
    if (!admin) return false;

    if (Array.isArray(role)) {
      return role.includes(admin.role);
    }
    return admin.role === role;
  }

  /**
   * Check if current admin is SuperAdmin
   */
  isSuperAdmin(): boolean {
    return this.hasRole('SuperAdmin');
  }

  /**
   * Check if current admin is active
   */
  isActive(): boolean {
    const admin = this.getCurrentAdmin();
    return admin ? admin.isActive : false;
  }

  /**
   * Get admin role weight for permission checking
   */
  getRoleWeight(): number {
    const admin = this.getCurrentAdmin();
    if (!admin) return 0;

    const roleWeights = {
      'Viewer': 1,
      'Moderator': 2,
      'SuperAdmin': 3
    };
    return roleWeights[admin.role] || 0;
  }

  /**
   * Check if current admin can modify another admin
   */
  canModifyAdmin(targetAdmin: Admin): boolean {
    const currentAdmin = this.getCurrentAdmin();
    if (!currentAdmin) return false;

    // SuperAdmins can modify anyone
    if (currentAdmin.role === 'SuperAdmin') {
      return true;
    }

    // Moderators can modify Viewers and themselves
    if (currentAdmin.role === 'Moderator') {
      return targetAdmin.role === 'Viewer' || currentAdmin._id === targetAdmin._id;
    }

    // Viewers can only modify themselves
    if (currentAdmin.role === 'Viewer') {
      return currentAdmin._id === targetAdmin._id;
    }

    return false;
  }

  /**
   * Clear local storage
   */
  private clearLocalStorage(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.ADMIN_KEY);
    this.currentAdminSubject.next(null);
  }

  // ================== PASSWORD UTILITIES ==================

  /**
   * Check password strength
   */
  checkPasswordStrength(password: string): { score: number; feedback: string[] } {
    const feedback: string[] = [];
    let score = 0;

    if (password.length >= 8) {
      score += 1;
    } else {
      feedback.push('Password should be at least 8 characters long');
    }

    if (/[a-z]/.test(password)) {
      score += 1;
    } else {
      feedback.push('Password should contain lowercase letters');
    }

    if (/[A-Z]/.test(password)) {
      score += 1;
    } else {
      feedback.push('Password should contain uppercase letters');
    }

    if (/\d/.test(password)) {
      score += 1;
    } else {
      feedback.push('Password should contain numbers');
    }

    if (/[^a-zA-Z\d]/.test(password)) {
      score += 1;
    } else {
      feedback.push('Password should contain special characters');
    }

    return { score, feedback };
  }

  /**
   * Generate secure password
   */
  generateSecurePassword(length: number = 12): string {
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const symbols = '@$!%*?&';
    
    let password = '';
    
    // Ensure at least one character from each category
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];
    
    // Fill the rest randomly
    const allChars = lowercase + uppercase + numbers + symbols;
    for (let i = password.length; i < length; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
  }
} 